/**
 * Mancala Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 *
 * Follows ReversiStatisticsService pattern.
 */

import {
  MancalaStatistics,
  MancalaGameResult,
  MancalaGameStats,
  MancalaDifficulty,
  MancalaGameSettings,
  PlayerSide,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class MancalaStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'mancala_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): MancalaStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): MancalaGameStats {
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      winPercentage: 0,
      averageScore: 0,
      highestScore: 0,
      totalSeedsCaptured: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): MancalaStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as MancalaStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get mancala statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(
    result: MancalaGameResult,
    playerSide: PlayerSide,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerSide);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, result, playerSide);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, result, playerSide);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record mancala game result:', error);
      return false;
    }
  }

  private updateGameStats(
    stats: MancalaGameStats,
    result: MancalaGameResult,
    playerSide: PlayerSide
  ): void {
    const totalGames = stats.wins + stats.losses + stats.draws;

    if (result.winner === 'draw') {
      stats.draws++;
    } else if (result.winner === playerSide) {
      stats.wins++;
    } else {
      stats.losses++;
    }

    const newTotalGames = stats.wins + stats.losses + stats.draws;
    stats.winPercentage = newTotalGames > 0 ? (stats.wins / newTotalGames) * 100 : 0;

    // Update average score
    const playerScore = result.score[playerSide];
    stats.averageScore =
      newTotalGames > 0
        ? (stats.averageScore * totalGames + playerScore) / newTotalGames
        : playerScore;

    // Update highest score
    if (playerScore > stats.highestScore) {
      stats.highestScore = playerScore;
    }

    // Update total captures
    stats.totalSeedsCaptured += result.totalCaptures;
  }

  /** Get statistics for a specific mode and difficulty. */
  getModeStatistics(
    mode: MancalaGameSettings['mode'],
    difficulty?: MancalaDifficulty,
    userId?: string
  ): MancalaGameStats {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'local-multiplayer') {
      return stats.localMultiplayer;
    } else if (mode === 'multiplayer-realtime') {
      return stats.onlineMultiplayer;
    }

    return this.createEmptyGameStats();
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset mancala statistics:', error);
      return false;
    }
  }
}
